"use client";

import { useId, useRef, useState, type FormEvent } from "react";

import Link from "@/components/ui/CcLink";
import { CcButton } from "@/components/ui/CcButton";
import { APP } from "@/lib/constants";
import { trackCampaignFormLead, type CampaignContext } from "@/lib/campaign-tracking";
import type { Lang } from "@/lib/types";

import type { CampaignFormCopy } from "./campaign-landing-page-copy";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type CcCampaignFormProps = {
  lang: Lang;
  copy: CampaignFormCopy;
  privacyHref: string;
  tracking: CampaignContext;
};

/** Same brand key the contact page sends (`CcContactForm`). */
function ourCompany(): string {
  const companyName = (APP.brand as { companyName?: string }).companyName ?? "";
  return companyName.split(" ")[0].toLowerCase();
}

function getVisitorJourney(): string | null {
  try {
    return localStorage.getItem("visitorJourney");
  } catch {
    return null;
  }
}

/**
 * The short alternative to booking: name, email, optional company and a
 * message. Posts to the same `/api/proxy` contact flow as the contact page
 * (ClickUp), with the page URL, including the `?amne=` ad group, as source.
 * The lead is tracked only once the backend confirms it was saved.
 */
export function CcCampaignForm({ lang, copy, privacyHref, tracking }: CcCampaignFormProps) {
  const id = useId();
  const pending = useRef(false);
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [invalid, setInvalid] = useState<"" | "fields" | "privacy">("");
  const [privacyBefore, privacyAfter] = copy.privacy.split("{link}");

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending.current) return;
    const data = new FormData(event.currentTarget);
    const field = (name: string) => String(data.get(name) ?? "").trim();
    const name = field("name");
    const email = field("email");
    const message = field("message");
    if (!name || !EMAIL_PATTERN.test(email) || !message) return setInvalid("fields");
    if (data.get("privacy") !== "on") return setInvalid("privacy");
    setInvalid("");

    pending.current = true;
    setStatus("sending");
    try {
      const response = await fetch("/api/proxy", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "formSubmission",
          name,
          email,
          company: field("company"),
          website: "",
          body: message,
          locale: lang,
          privacyPolicyAccepted: true,
          language: navigator.language,
          tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
          source: window.location.href,
          journey: getVisitorJourney(),
          ourCompany: ourCompany(),
        }),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok || result?.received !== true) {
        setStatus("error");
        return;
      }
      setStatus("sent");
      trackCampaignFormLead(lang, tracking);
    } catch {
      setStatus("error");
    } finally {
      pending.current = false;
    }
  }

  if (status === "sent") {
    return (
      <div className="cc-campaign-form cc-campaign-form--sent" role="status">
        <p className="cc-campaign-form__success-title">{copy.successTitle}</p>
        <p>{copy.successText}</p>
      </div>
    );
  }

  return (
    <form className="cc-campaign-form" onSubmit={onSubmit} noValidate data-campaign-form data-analytics-form="campaign_form">
      <div className="cc-campaign-form__row">
        <label htmlFor={`${id}-name`}>
          {copy.name}
          <input id={`${id}-name`} name="name" autoComplete="name" required />
        </label>
        <label htmlFor={`${id}-email`}>
          {copy.email}
          <input id={`${id}-email`} name="email" type="email" autoComplete="email" required />
        </label>
      </div>
      <label htmlFor={`${id}-company`}>
        <span>
          {copy.company} <small>{copy.optional}</small>
        </span>
        <input id={`${id}-company`} name="company" autoComplete="organization" />
      </label>
      <label htmlFor={`${id}-message`}>
        {copy.message}
        <textarea id={`${id}-message`} name="message" rows={4} placeholder={copy.messagePlaceholder} required />
      </label>
      <label className="cc-campaign-form__privacy" htmlFor={`${id}-privacy`}>
        <input id={`${id}-privacy`} name="privacy" type="checkbox" required />
        <span>
          {privacyBefore}
          <Link href={privacyHref}>{copy.privacyLink}</Link>
          {privacyAfter}
        </span>
      </label>
      {invalid || status === "error" ? (
        <p className="cc-campaign-form__error" role="alert">
          {status === "error" ? copy.error : invalid === "privacy" ? copy.privacyRequired : copy.required}
        </p>
      ) : null}
      <CcButton
        type="submit"
        label={status === "sending" ? copy.sending : copy.submit}
        loading={status === "sending"}
        disabled={status === "sending"}
        expand
        expandCompact
      />
    </form>
  );
}
